import {
  ASSESSMENT_CATEGORIES,
  type Assessment,
  type AssessmentCategoryKey,
} from "../assessment-types";
import { ASSESSMENT_TAXONOMY } from "./taxonomy";
import { TRAUMA_RESPONSES_ASSESSMENT } from "./trauma-responses";
import { PEOPLE_PLEASING_ASSESSMENT } from "./people-pleasing";
import { BOUNDARIES_ASSESSMENT } from "./boundaries";
import { HYPERVIGILANCE_ASSESSMENT } from "./hypervigilance";
import { EMOTIONAL_REGULATION_ASSESSMENT } from "./emotional-regulation";
import { NERVOUS_SYSTEM_ASSESSMENT } from "./nervous-system";
import { DISSOCIATION_ASSESSMENT } from "./dissociation";
import { NUMBNESS_ASSESSMENT } from "./numbness";
import { ATTACHMENT_ASSESSMENT } from "./attachment";
import { TRAUMA_BONDS_ASSESSMENT } from "./trauma-bonds";
import { SELF_WORTH_ASSESSMENT } from "./self-worth";
import { RELATIONSHIPS_ASSESSMENT } from "./relationships";
import {
  TRIGGER_PATTERNS_ASSESSMENT,
  FUNCTIONAL_FREEZE_ASSESSMENT,
  SURVIVAL_MODE_ASSESSMENT,
} from "./expansion-nervous-system";
import {
  EMOTIONAL_FLASHBACKS_ASSESSMENT,
  ANXIETY_PATTERNS_ASSESSMENT,
  SELF_COMPASSION_ASSESSMENT,
} from "./expansion-emotions";
import {
  FEAR_OF_ABANDONMENT_ASSESSMENT,
  TRUST_AFTER_TRAUMA_ASSESSMENT,
  RELATIONSHIP_SAFETY_ASSESSMENT,
} from "./expansion-relationships";
import {
  OVERTHINKING_ASSESSMENT,
  INNER_CRITIC_ASSESSMENT,
  AVOIDANCE_ASSESSMENT,
  HYPER_INDEPENDENCE_ASSESSMENT,
} from "./expansion-thoughts";
import {
  CHILDHOOD_EMOTIONAL_NEGLECT_ASSESSMENT,
  SLEEP_AND_TRAUMA_ASSESSMENT,
} from "./expansion-self-daily";

/**
 * Registry of educational self-assessments.
 *
 * Adding an assessment here makes it routable and makes it appear
 * automatically in the category browser, the A–Z directory and search —
 * no page needs to be edited. Phase 2 (strength-based) assessments can be
 * added the same way with `phase: 2` on the assessment.
 */
export const ALL_ASSESSMENTS: Assessment[] = [
  TRAUMA_RESPONSES_ASSESSMENT,
  PEOPLE_PLEASING_ASSESSMENT,
  BOUNDARIES_ASSESSMENT,
  HYPERVIGILANCE_ASSESSMENT,
  EMOTIONAL_REGULATION_ASSESSMENT,
  NERVOUS_SYSTEM_ASSESSMENT,
  DISSOCIATION_ASSESSMENT,
  NUMBNESS_ASSESSMENT,
  ATTACHMENT_ASSESSMENT,
  TRAUMA_BONDS_ASSESSMENT,
  SELF_WORTH_ASSESSMENT,
  RELATIONSHIPS_ASSESSMENT,
  TRIGGER_PATTERNS_ASSESSMENT,
  FUNCTIONAL_FREEZE_ASSESSMENT,
  SURVIVAL_MODE_ASSESSMENT,
  EMOTIONAL_FLASHBACKS_ASSESSMENT,
  ANXIETY_PATTERNS_ASSESSMENT,
  SELF_COMPASSION_ASSESSMENT,
  FEAR_OF_ABANDONMENT_ASSESSMENT,
  TRUST_AFTER_TRAUMA_ASSESSMENT,
  RELATIONSHIP_SAFETY_ASSESSMENT,
  OVERTHINKING_ASSESSMENT,
  INNER_CRITIC_ASSESSMENT,
  AVOIDANCE_ASSESSMENT,
  HYPER_INDEPENDENCE_ASSESSMENT,
  CHILDHOOD_EMOTIONAL_NEGLECT_ASSESSMENT,
  SLEEP_AND_TRAUMA_ASSESSMENT,
];

export function getAssessment(slug: string): Assessment | null {
  return ALL_ASSESSMENTS.find((a) => a.slug === slug) ?? null;
}

/** Category of an assessment, falling back to the taxonomy map. */
export function categoryOf(a: Assessment): AssessmentCategoryKey {
  return a.category ?? ASSESSMENT_TAXONOMY[a.slug]?.category ?? "nervous-system";
}

/** Search terms for an assessment: title, tagline, intro and keywords. */
export function searchTermsOf(a: Assessment): string {
  const extra = a.keywords ?? ASSESSMENT_TAXONOMY[a.slug]?.keywords ?? [];
  return [a.title, a.tagline, a.intro, ...extra].join(" ").toLowerCase();
}

/** Alphabetical list, ignoring leading articles. */
export function sortedAssessments(list: Assessment[] = ALL_ASSESSMENTS): Assessment[] {
  return [...list].sort((a, b) => a.title.localeCompare(b.title));
}

/** First letter used by the A–Z directory. */
export function letterOf(a: Assessment): string {
  return a.title.charAt(0).toUpperCase();
}

/** Assessments grouped by category, in the display order of the categories. */
export function assessmentsByCategory(list: Assessment[] = ALL_ASSESSMENTS) {
  return ASSESSMENT_CATEGORIES.map((category) => ({
    category,
    items: list.filter((a) => categoryOf(a) === category.key),
  })).filter((g) => g.items.length > 0);
}

export function searchAssessments(query: string, list: Assessment[] = ALL_ASSESSMENTS) {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  const words = q.split(/\s+/);
  return list.filter((a) => {
    const haystack = searchTermsOf(a);
    return words.every((w) => haystack.includes(w));
  });
}
